import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Search, Code, Trash2, Edit3, Terminal, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import api from './axiosConfig';

// 🎯 نفس شكل ActionScriptList.js القديمة بالظبط (استايل داكن مميز للاستوديو)
// بس مربوطة بموديل كستم واحد بس (slug) ومحرك تنفيذ جديد منفصل تمامًا -
// شوف app/services/custom_script_engine.py في الباك إند. الوصول هنا
// SUPER_ADMIN بس نظرًا لخطورة تنفيذ بايثون عشوائي.
const CustomActionScriptList = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const navigate = useNavigate();
  const { slug } = useParams();

  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchScripts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/custom-models/${slug}/scripts`);
      setScripts(res.data || []);
    } catch (err) {
      toast.error(t('scripts.toasts.fetch_fail', 'Failed to load scripts'));
    } finally {
      setLoading(false);
    }
  }, [slug, t]);

  useEffect(() => { fetchScripts(); }, [fetchScripts]);

  const handleDelete = async (scriptId) => {
    try {
      await api.delete(`/custom-action-scripts/${scriptId}`);
      toast.success(t('common.deleted', 'Deleted'));
      fetchScripts();
    } catch (err) {
      toast.error(t('common.deleteFailed', 'Delete failed'));
    }
  };

  const filtered = scripts.filter((s) => s.script_name.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className={`min-h-screen bg-[#0f172a] text-slate-200 p-8 ${isRTL ? 'font-cairo' : 'font-sans'}`}>
      <div className="max-w-6xl mx-auto">
        <div className={`flex justify-between items-center mb-10 ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}>
          <div>
            <button onClick={() => navigate(`/admin/custom-apps/${slug}/studio`)} className="text-slate-500 hover:text-white flex items-center gap-2 text-[10px] font-black uppercase mb-2 transition-colors">
              <ArrowLeft size={14} /> {t('customApps.backToStudio', 'Back to fields')}
            </button>
            <h1 className={`text-3xl font-black text-white flex items-center gap-3 tracking-tighter ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Terminal className="text-orange-500" size={32} /> {t('customApps.scripts.title', 'Action Scripts')} — {slug}
            </h1>
          </div>
          <button
            onClick={() => navigate(`/admin/custom-apps/${slug}/scripts/new`)}
            className="bg-orange-600 hover:bg-orange-500 text-white px-6 py-3 rounded-md font-black flex items-center gap-2 transition-all shadow-lg hover:scale-105 uppercase text-xs"
          >
            <Plus size={18} /> {t('scripts.new_btn', 'New Script')}
          </button>
        </div>

        <div className="bg-[#1e293b] border border-slate-700 rounded-xl overflow-hidden shadow-2xl">
          <div className={`p-4 border-b border-slate-700 flex items-center gap-4 bg-[#1e293b]/50 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Search size={18} className="text-slate-500" />
            <input
              placeholder={t('scripts.search_placeholder', 'Search scripts...')}
              className={`bg-transparent border-none outline-none text-sm w-full font-semibold ${isRTL ? 'text-right' : 'text-left'}`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="overflow-x-auto">
            <table className={`w-full ${isRTL ? 'text-right' : 'text-left'}`}>
              <thead className="bg-[#0f172a]/50 text-[10px] font-black uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="p-4">{t('scripts.table.name', 'Name')}</th>
                  <th className="p-4">{t('customApps.scripts.event', 'Event')}</th>
                  <th className="p-4">{t('workflow.table.status', 'Status')}</th>
                  <th className="p-4 text-center">{t('workflow.action', 'Action')}</th>
                </tr>
              </thead>
              <tbody className={`divide-y divide-slate-700 ${loading ? 'opacity-50' : ''}`}>
                {filtered.length > 0 ? filtered.map((script) => (
                  <tr key={script.id} className="hover:bg-slate-800/50 transition-colors group">
                    <td className="p-4">
                      <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className="p-2 bg-orange-500/10 rounded-lg text-orange-500"><Code size={18} /></div>
                        <span className="font-bold text-white uppercase">{script.script_name}</span>
                      </div>
                    </td>
                    <td className="p-4 text-xs font-black text-blue-400 uppercase tracking-tighter">{script.event}</td>
                    <td className="p-4">
                      <span className={`text-[9px] px-2 py-1 rounded-full font-black uppercase ${script.is_active ? 'bg-green-500/10 text-green-500' : 'bg-slate-700 text-slate-400'}`}>
                        {script.is_active ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className={`flex justify-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <button onClick={() => navigate(`/admin/custom-apps/${slug}/scripts/${script.id}`)} className="p-2 hover:bg-slate-700 rounded-md text-slate-400 hover:text-white transition-all"><Edit3 size={16} /></button>
                        <button onClick={() => handleDelete(script.id)} className="p-2 hover:bg-red-500/10 rounded-md text-slate-500 hover:text-red-500 transition-all"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="4" className="p-20 text-center text-slate-500 font-bold uppercase italic tracking-widest">{t('dashboard.no_data', 'No data')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomActionScriptList;
